import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { InvestorMessageRes } from '@dealstack/shared/lib/generated';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';

interface Options
	extends Omit<
		UseQueryOptions<
			ApiV1.Response<InvestorMessageRes>,
			unknown,
			InvestorMessageRes,
			[string, { investorId: string; messageId: string; mipId: string }]
		>,
		'queryKey' | 'queryFn'
	> {
	investorId: string;
	mipId: string;
	messageId: string;
}

const getMessage = async (
	endpoint: string,
	args: { investorId: string; messageId: string; mipId: string }
) => {
	const { investorId, messageId, mipId } = args;
	return await makeRequest<ApiV1.Response<InvestorMessageRes>>({
		endpoint: formatUnicorn(endpoint, {
			investorId,
			messageId,
			mipId,
		}),
		method: HTTP_METHOD.GET,
	});
};

export const useMessage = (
	options: Options
): UseQueryResult<InvestorMessageRes | undefined, unknown> => {
	const { investorId, messageId, mipId, ...queryOptions } = options;
	return useQuery({
		queryKey: [
			API_PATHS.MIP_MIPID_INVESTOR_INVESTORID_MESSAGES_MESSAGEID_GET,
			{ investorId, messageId, mipId },
		],
		queryFn: () =>
			getMessage(API_PATHS.MIP_MIPID_INVESTOR_INVESTORID_MESSAGES_MESSAGEID_GET, {
				investorId,
				messageId,
				mipId,
			}),
		...queryOptions,
		select: (data) => {
			const message = data.payload;
			const formattedMessage = message?.sendHistory.map((historyItem) => {
				return {
					...historyItem,
					createdAt: formatDateToUI(
						new Date(historyItem.createdAt),
						DATE_FORMATS.DATE_TIME_ALT
					),
				};
			});
			return {
				...message,
				sendDate: message?.sendDate
					? formatDateToUI(new Date(message?.sendDate), DATE_FORMATS.DATE_TIME_ALT)
					: '',
				sendHistory: formattedMessage,
			} as InvestorMessageRes;
		},
	});
};
